// src/utils/image.js
import heic2any from 'heic2any';
import imageCompression from 'browser-image-compression';

/**
 * Normaliza la imagen para subirla:
 * - Convierte HEIC/HEIF a JPEG
 * - (Opcional) fuerza JPEG para reducir tamaño (mejor compresión que PNG)
 * - Corrige orientación EXIF
 * - Comprime a ~1.2 MB y máx. 1600px lado mayor
 */
export async function normalizeImage(file) {
  let working = file;

  try {
    // 1) HEIC/HEIF -> JPEG
    const isHeic =
      /image\/hei[cf]/i.test(file.type) ||
      /\.(heic|heif)$/i.test(file.name || '');

    if (isHeic) {
      const blob = await heic2any({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.9,
      });
      working = new File(
        [blob],
        (file.name || 'foto').replace(/\.(heic|heif)$/i, '.jpg'),
        { type: 'image/jpeg' }
      );
    }

    // 2) (Opcional) si no es vector (SVG) ni WebP, fuerzo JPEG para reducir
    //    *Si necesitas conservar transparencia en PNG, quita fileType.
    const forceJpeg = working.type !== 'image/svg+xml' && working.type !== 'image/webp';

    // 3) Comprimir y corregir orientación
    const compressed = await imageCompression(working, {
      maxSizeMB: 1.2,           // objetivo: ~1.2 MB (ajústalo a tu límite)
      maxWidthOrHeight: 1600,   // reduce resolución si es muy grande
      useWebWorker: true,
      maxIteration: 10,
      initialQuality: 0.85,
      ...(forceJpeg ? { fileType: 'image/jpeg' } : {}),
    });

    // 4) Asegura que sea File con nombre ".jpg" cuando sea JPEG
    if (compressed instanceof Blob && !(compressed instanceof File)) {
      const ext = (forceJpeg || compressed.type === 'image/jpeg') ? '.jpg' : (guessExt(compressed.type) || '.jpg');
      const name = ensureNameWithExt(working.name || 'foto', ext);
      return new File([compressed], name, { type: compressed.type || 'image/jpeg' });
    }

    // browser-image-compression suele devolver File, pero normalizamos nombre
    if (compressed instanceof File) {
      const needsJpgName = (compressed.type === 'image/jpeg') && !/\.jpe?g$/i.test(compressed.name || '');
      if (needsJpgName) {
        return new File([compressed], ensureNameWithExt(compressed.name || 'foto', '.jpg'), { type: 'image/jpeg' });
      }
      return compressed;
    }

    // Fallback improbable
    return working;
  } catch (err) {
    console.warn('normalizeImage falló, usando original:', err);
    // No bloquees el flujo por error al convertir/comprimir
    return working;
  }
}

function ensureNameWithExt(name, ext) {
  const clean = (name || 'foto').replace(/\.[^.]+$/,''); // quita extensión existente
  return `${clean}${ext}`;
}
function guessExt(mime) {
  switch (mime) {
    case 'image/jpeg': return '.jpg';
    case 'image/png':  return '.png';
    case 'image/webp': return '.webp';
    case 'image/svg+xml': return '.svg';
    case 'image/heic': return '.heic';
    case 'image/heif': return '.heif';
    default: return '';
  }
}
